import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, DollarSign, PlusCircle, MinusCircle, RotateCcw, AlertTriangle } from 'lucide-react';
import { accountingAPI, agentAPI, agencyAPI } from '@/lib/api';
import { toast } from 'sonner';

const TYPES = [
  { v: 'manual_payment',  label: 'Manual Payment',  desc: 'Bonus, reimbursement, incentive — recorded outside a batch', icon: DollarSign,   tone: 'red' },
  { v: 'manual_credit',   label: 'Manual Credit',   desc: 'Increase recipient balance (correction, courtesy credit)',   icon: PlusCircle,   tone: 'emerald' },
  { v: 'manual_debit',    label: 'Manual Debit',    desc: 'Reduce recipient balance (chargeback, correction, fee)',    icon: MinusCircle,  tone: 'orange' },
  { v: 'manual_recovery', label: 'Manual Recovery', desc: 'Money recovered — ACH pull, returned advance, repayment',    icon: RotateCcw,    tone: 'blue' },
];

const PAYMENT_METHODS = [
  { v: 'ach',   label: 'ACH' },
  { v: 'check', label: 'Check' },
  { v: 'cash',  label: 'Cash' },
  { v: 'other', label: 'Other' },
];

export default function NewAdjustmentDialog({ open, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [type, setType] = useState('manual_credit');
  const [recipientType, setRecipientType] = useState('agent');
  const [recipientQuery, setRecipientQuery] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState(null); // {id, name}
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('ach');
  const [paymentReference, setPaymentReference] = useState('');
  const [saving, setSaving] = useState(false);
  const [eligibility, setEligibility] = useState(null);

  useEffect(() => {
    if (!open) {
      setStep(1); setType('manual_credit'); setRecipientType('agent'); setRecipientQuery('');
      setSelectedRecipient(null); setAmount(''); setReason(''); setNotes('');
      setPaymentDate(''); setPaymentMethod('ach'); setPaymentReference(''); setEligibility(null);
    }
  }, [open]);

  const search = useCallback(async () => {
    if (!recipientQuery || recipientQuery.length < 2) { setCandidates([]); return; }
    try {
      if (recipientType === 'agent') {
        const { data } = await agentAPI.list({ search: recipientQuery, limit: 10 });
        const items = data.items || data || [];
        setCandidates(items.slice(0, 10).map((a) => ({
          id: a.user_id || a.npn,
          name: `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.email || a.npn,
        })));
      } else {
        const { data } = await agencyAPI.list();
        const items = (data.items || data || []).filter((a) =>
          (a.agency_name || '').toLowerCase().includes(recipientQuery.toLowerCase())
        );
        setCandidates(items.slice(0, 10).map((a) => ({ id: a.agency_id, name: a.agency_name })));
      }
    } catch { setCandidates([]); }
  }, [recipientQuery, recipientType]);

  useEffect(() => { const t = setTimeout(search, 300); return () => clearTimeout(t); }, [search]);

  const goNext = () => setStep((s) => Math.min(3, s + 1));
  const goBack = () => setStep((s) => Math.max(1, s - 1));

  const submit = async (saveAsDraft = true) => {
    if (!selectedRecipient) return toast.error('Select a recipient');
    if (!amount || Number(amount) <= 0) return toast.error('Amount must be positive');
    if (!reason.trim()) return toast.error('Reason is required');
    if (type === 'manual_payment') {
      if (!paymentDate) return toast.error('Payment date required');
      if (!paymentMethod) return toast.error('Payment method required');
    }
    setSaving(true);
    try {
      const payload = {
        adjustment_type: type,
        recipient_type: recipientType,
        recipient_id: selectedRecipient.id,
        recipient_name: selectedRecipient.name,
        amount: Number(amount),
        reason: reason.trim(),
        notes: notes.trim() || undefined,
      };
      if (type === 'manual_payment') {
        payload.payment_date = paymentDate;
        payload.payment_method = paymentMethod;
        payload.payment_reference = paymentReference.trim() || undefined;
      }
      const { data } = await accountingAPI.createAdjustment(payload);
      if (data.recipient_blocked) {
        toast.warning(`Recipient is blocked (${data.blocking_reasons.join(', ')}). Draft saved — KR approval required.`);
      } else {
        toast.success('Adjustment saved as draft');
      }
      onCreated?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create adjustment');
    } finally { setSaving(false); }
  };

  const canProceedFrom1 = !!type;
  const canProceedFrom2 = !!selectedRecipient;
  const T = TYPES.find((t) => t.v === type);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="new-adj-dialog">
        <DialogHeader>
          <DialogTitle>New Manual Adjustment — Step {step} of 3</DialogTitle>
          <DialogDescription>
            {step === 1 && 'Choose the adjustment type.'}
            {step === 2 && 'Select the recipient.'}
            {step === 3 && 'Enter amount, reason and details.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2">
          {step === 1 && (
            <div className="space-y-2">
              {TYPES.map((t) => {
                const Icon = t.icon;
                const isActive = t.v === type;
                return (
                  <button
                    key={t.v}
                    onClick={() => setType(t.v)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition flex items-start gap-3 ${
                      isActive ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:border-slate-400'
                    }`}
                    data-testid={`type-option-${t.v}`}
                  >
                    <div className={`p-2 rounded-md bg-${t.tone}-50 text-${t.tone}-700`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">{t.label}</div>
                      <div className="text-xs text-slate-600 mt-0.5">{t.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <div className="flex gap-2">
                {['agent', 'agency', 'subagency'].map((rt) => (
                  <button key={rt} onClick={() => { setRecipientType(rt); setSelectedRecipient(null); setCandidates([]); setRecipientQuery(''); }}
                    className={`text-xs px-3 py-1.5 rounded-full border ${recipientType === rt ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-200'}`}
                    data-testid={`recipient-type-${rt}`}>
                    {rt.charAt(0).toUpperCase() + rt.slice(1)}
                  </button>
                ))}
              </div>
              <Input
                value={recipientQuery}
                onChange={(e) => setRecipientQuery(e.target.value)}
                placeholder={`Search ${recipientType}s by name…`}
                data-testid="recipient-search"
              />
              {candidates.length > 0 && (
                <div className="border rounded-md divide-y max-h-60 overflow-y-auto">
                  {candidates.map((c) => (
                    <button key={c.id} onClick={() => { setSelectedRecipient(c); setCandidates([]); setRecipientQuery(c.name); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
                      data-testid={`recipient-option-${c.id}`}>
                      {c.name}
                    </button>
                  ))}
                </div>
              )}
              {selectedRecipient && (
                <div className="mt-2 p-2 bg-emerald-50 border border-emerald-200 rounded-md text-sm text-emerald-800">
                  Selected: <b>{selectedRecipient.name}</b>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
                <b>{T?.label}</b> to <b>{selectedRecipient?.name}</b>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Amount (USD)</Label>
                  <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" data-testid="adj-amount" />
                </div>
                {type === 'manual_payment' && (
                  <div>
                    <Label>Payment Date</Label>
                    <Input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} data-testid="adj-payment-date" />
                  </div>
                )}
              </div>

              {type === 'manual_payment' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Payment Method</Label>
                    <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                      <SelectTrigger data-testid="adj-payment-method"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PAYMENT_METHODS.map((m) => (<SelectItem key={m.v} value={m.v}>{m.label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Reference (optional)</Label>
                    <Input value={paymentReference} onChange={(e) => setPaymentReference(e.target.value)} placeholder="Check #, ACH ref…" />
                  </div>
                </div>
              )}

              <div>
                <Label>Reason</Label>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Referral bonus, chargeback, correction…" data-testid="adj-reason" />
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="adj-notes" />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          {step > 1 && <Button variant="ghost" onClick={goBack} disabled={saving}>Back</Button>}
          {step < 3 && (
            <Button
              onClick={goNext}
              disabled={(step === 1 && !canProceedFrom1) || (step === 2 && !canProceedFrom2)}
              data-testid="next-btn"
            >
              Next
            </Button>
          )}
          {step === 3 && (
            <Button onClick={() => submit(true)} disabled={saving} data-testid="save-draft-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Draft
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
